import { CaseDeletabilityData } from 'api/cases/types';

export type DeleteButtonState =
  | 'hidden'
  | 'loading'
  | 'blocked'
  | 'warn'
  | 'ready';

/**
 * Maps the deletability endpoint's state onto the DELETE button's visual state
 * (design §6). Deletability truth comes from the endpoint only - never inferred
 * from the loaded case (it carries no reliable comment count).
 */
export const deriveDeleteButtonState = (
  hasSourceCase: boolean,
  loading: boolean,
  deletability: CaseDeletabilityData | null,
): DeleteButtonState => {
  if (!hasSourceCase) return 'hidden';
  if (loading) return 'loading';
  if (!deletability || deletability.state === 'NOT_FOUND') return 'hidden';
  if (deletability.state === 'WARN_ATTACHMENTS') return 'warn';
  if (deletability.state === 'DELETABLE') return 'ready';
  return 'blocked';
};

/** Tooltip explaining WHY the button is disabled (UI_UX_STANDARDS: every disabled control needs one). */
export const deriveDeleteBlockedTooltip = (
  deletability: CaseDeletabilityData | null,
): string => {
  switch (deletability?.state) {
    case 'BLOCKED_COMMENTS':
      // Deliberately count-free: the raw taskPath/groupPath numbers are backend
      // internals and must never surface in the tooltip (FE-DEFECT-1,
      // 2026-07-21). The deletability payload still carries them.
      return 'A case with comments cannot be deleted.';
    case 'BLOCKED_TEMPLATE':
      return `This case owns a report template used by ${deletability.flags.templateConsumers} other case(s) and cannot be deleted.`;
    case 'BLOCKED_TYPE':
      return 'This case type cannot be deleted.';
    default:
      return '';
  }
};

/** Surfaces the backend { error: { code, message } } envelope; falls back for network/unknown errors. */
export const getServerErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  const e = error as {
    response?: { data?: { error?: { message?: string } } };
  };
  return e?.response?.data?.error?.message ?? fallback;
};
